import type { SettledError, SettledResponse } from '@avantstay/graphql-ts-client'

import { trackError } from '@tracking/customEventReport'

import { backofficeRouter } from './backofficeRouter'

// Codes that answer the request rather than report a failure: auth, permissions and missing records.
// Every other code is reported, including ones added later and the router's `SUBREQUEST_HTTP_ERROR`.
const EXPECTED_ERROR_CODES = new Set([
  'UNAUTHENTICATED',
  'UNAUTHORISED',
  'FORBIDDEN',
  'ACCESS_DENIED',
  'NOT_FOUND',
  'RESOURCE_NOT_FOUND',
])
const UNHANDLED_ERROR_MESSAGE = 'Internal server error'

interface SettledDataOrThrowParams<Data> {
  result: SettledResponse<Data>
  queryName: string
}

/**
 * Errors without a code are business rejections when the subgraph forwards its message, so only
 * the router's redacted placeholder and sangria's message for an unhandled exception are faults.
 * The router forwards messages for monolith, analytics and the other subgraphs in `router.yml`.
 */
const isServerFault = ({ extensions, message, redacted }: SettledError) => {
  const code = extensions?.code

  if (code) return !EXPECTED_ERROR_CODES.has(code)

  return redacted || message.startsWith(UNHANDLED_ERROR_MESSAGE)
}

export const settledDataOrThrow = <Data>({ result, queryName }: SettledDataOrThrowParams<Data>) => {
  if (result.outcome === 'failure') {
    throw new Error(result.errors.at(-1)?.message ?? `${queryName} failed: ${result.reason}`)
  }

  const serverFaults = result.errors.filter(isServerFault)

  // TODO: move this report to arriereResponseListener so it also covers the direct calls.
  if (serverFaults.length) {
    trackError(
      'Arriere partial response',
      {
        queryName,
        router: backofficeRouter.router,
        failedPaths: result.failedPaths,
        errors: serverFaults.map(({ message, path, extensions }) => ({
          message,
          path: path?.join('.'),
          code: extensions?.code,
          requestId: extensions?.requestId,
        })),
      },
      false,
    )
  }

  return result.data
}
